// Lib
import { clamp } from 'lodash/fp';

// Utils
import {
    getDistToScrollToBottomEdge,
    getDistToScrollToLeftEdge,
    getDistToScrollToRightEdge,
    getDistToScrollToTopEdge,
    getScrollXMax,
    getScrollXMin,
    getScrollYMax,
    getScrollYMin,
    getViewportRect,
    ViewportOptions,
} from './scrollableParentUtils';

// Types
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';

type ScrollOffsets = {
    scrollLeft: number;
    scrollTop: number;
};

export const getScrollLeft = ({
    scrollableParent,
    viewportRect,
    targetRect,
}: {
    scrollableParent: HTMLElement;
    viewportRect: Rect;
    targetRect: Rect;
}): number => {
    const scrollRange = clamp(getScrollXMin(scrollableParent), getScrollXMax(scrollableParent));

    const scrollLeftX = getDistToScrollToLeftEdge({ viewportRect, targetRect });
    const scrollRightX = getDistToScrollToRightEdge({ viewportRect, targetRect });

    // Don't need to scroll X if the target is already within the X range
    if (scrollLeftX >= 0 && scrollRightX <= 0) return scrollableParent.scrollLeft;

    const scrollDiffX = scrollRange(
        Math.min(
            scrollLeftX,
            // If we're scrolling left, don't attempt to scroll to the right edge
            scrollLeftX < 0 ? Infinity : scrollRightX,
        ),
    );

    // Math.min will ensure the left edge is favoured if it won't fit in the viewport
    return scrollableParent.scrollLeft + scrollDiffX;
};

export const getScrollTop = ({
    scrollableParent,
    viewportRect,
    targetRect,
}: {
    scrollableParent: HTMLElement;
    viewportRect: Rect;
    targetRect: Rect;
}): number => {
    const scrollRange = clamp(getScrollYMin(scrollableParent), getScrollYMax(scrollableParent));

    const scrollTopY = getDistToScrollToTopEdge({ viewportRect, targetRect });
    // If we're scrolling up, don't attempt to scroll to the bottom edge
    const scrollBottomY = getDistToScrollToBottomEdge({ viewportRect, targetRect });

    // Don't need to scroll X if the target is already within the X range
    if (scrollTopY >= 0 && scrollBottomY <= 0) return scrollableParent.scrollTop;

    const scrollDiffY = scrollRange(
        Math.min(
            scrollTopY,
            // If we're scrolling left, don't attempt to scroll to the right edge
            scrollTopY < 0 ? Infinity : scrollBottomY,
        ),
    );

    // Math.min will ensure the top edge is favoured if it won't fit in the viewport
    return scrollableParent.scrollTop + scrollDiffY;
};

/**
 * Gets the updated scrollLeft and scrollTop to get the scrollableParent to show the targetRect.
 */
export const getScrollForTarget = ({
    scrollableParent,
    targetRect,
    viewportOptions,
}: {
    scrollableParent: HTMLElement;
    targetRect: Rect;
    viewportOptions?: ViewportOptions;
}): ScrollOffsets => {
    const viewportRect = getViewportRect(scrollableParent, viewportOptions);

    return {
        scrollLeft: getScrollLeft({ scrollableParent, viewportRect, targetRect }),
        scrollTop: getScrollTop({ scrollableParent, viewportRect, targetRect }),
    };
};

/**
 * Gets the updated scrollLeft and scrollTop to always align the target's top-left corner with the
 * viewport's top-left corner - unlike getScrollForTarget, this has no "already in view" early-return
 * and no oversized-vs-normal branching, since the right/bottom edges are never considered.
 */
export const getScrollToAlignTargetTopLeft = ({
    scrollableParent,
    targetRect,
    viewportOptions,
}: {
    scrollableParent: HTMLElement;
    targetRect: Rect;
    viewportOptions?: ViewportOptions;
}): ScrollOffsets => {
    const viewportRect = getViewportRect(scrollableParent, viewportOptions);

    const scrollLeftDiff = getDistToScrollToLeftEdge({ viewportRect, targetRect });
    const scrollTopDiff = getDistToScrollToTopEdge({ viewportRect, targetRect });

    const scrollLeftRange = clamp(getScrollXMin(scrollableParent), getScrollXMax(scrollableParent));
    const scrollTopRange = clamp(getScrollYMin(scrollableParent), getScrollYMax(scrollableParent));

    return {
        scrollLeft: scrollableParent.scrollLeft + scrollLeftRange(scrollLeftDiff),
        scrollTop: scrollableParent.scrollTop + scrollTopRange(scrollTopDiff),
    };
};
